import { Control, Controller, UseFieldArrayRemove, UseFormReturn } from "react-hook-form";
import { BorderWithLabel } from "./BorderWithLabel";
import { LocationInput } from "../../location/LocationInput";
import { Box, Button, Grid, IconButton, Tooltip } from "@mui/material";
import React, { ReactNode, useState } from "react";
import { RouteRequestFormData } from "./TripFormTypeConversions";
import MenuIcon from "@mui/icons-material/Menu";
import ClearIcon from "@mui/icons-material/Clear";
import {
    EDIT_LENGTH_OF_STAY_TOOLTIP,
    END_STRING,
    LOCATION,
    LOCATION_TOOLTIP,
    OVERNIGHT_STOP,
    PIT_STOP_STRING,
    SPECIFY_EARLY_OR_LATE_START,
    START_STRING
} from "../../UserVisibleStrings";
import { LengthOfStayDialog } from "./LengthOfStayDialog";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { removeWaypoint, updateWaypoint } from "../../../features/riskViewer/riskViewerSlice";
import { useIsMobile } from "../../../features/pageWidth/pageWidthSlice";
import { useDispatch } from "react-redux";
import OpenLayersModal from "../../location/LocationSelectModal";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useSelectedWaypointInputIndex } from "../../../features/Drawer/drawerSlice";
import { faLocationDot } from "@fortawesome/sharp-duotone-light-svg-icons";
import { DayOneStartDialog } from "./DayOneStartDialog";

type DraggableWaypointProps = {
    backgroundColor: string,
    control: Control<RouteRequestFormData>,
    index: number,
    locationName: string,
    errorProp: any, // eslint-disable-line @typescript-eslint/no-explicit-any
    formParent: UseFormReturn<RouteRequestFormData>,
    isFirst: boolean,
    isLast: boolean,
    muiTextFieldRegister: any, // eslint-disable-line @typescript-eslint/no-explicit-any
    handleRemove: UseFieldArrayRemove,
    waypointSize: number,
    setIsInteractingWithTextFieldLocationInput: React.Dispatch<React.SetStateAction<boolean>>,
    isAdvisory: boolean,
};

type CustomGridContainerProps = {
    first: ReactNode,
    second: ReactNode,
};
export const CustomGridContainer = ( { first, second }: CustomGridContainerProps ) =>
{
    return (
        <Grid container sx = {{ height: "30px", marginTop: "-5px" }}>
            <Grid item xs = {6}>
                {first}
            </Grid>
            <Grid item xs = {6}>
                {second}
            </Grid>
        </Grid>
    );
};

export const DraggableWaypoint = ( {
    control,
    index,
    locationName,
    errorProp,
    formParent,
    isFirst,
    isLast,
    handleRemove,
    muiTextFieldRegister,
    waypointSize,
    setIsInteractingWithTextFieldLocationInput,
    backgroundColor,
    isAdvisory
}: DraggableWaypointProps ) =>
{
    const { register } = formParent;
    const isIntermediate = !isFirst && !isLast;
    const isDefined = formParent.getValues( `waypoints.${index}.location.locationName` ) !== "";

    const [ isHovered, setIsHovered ] = useState( false );
    const isMobile = useIsMobile();
    const dispatch = useDispatch();
    const drag = (
        <MenuIcon
            sx = {{
                display: "table",
                visibility: isHovered || isMobile ? "visible" : "hidden",
                marginLeft: "-15px",
                marginTop: "-15px"
            }}
        />
    );

    const [ modalOpen, setModalOpen ] = useState( false );

    const handleModalOpen = () => setModalOpen( true );
    const handleModalClose = () => setModalOpen( false );
    const [ dialogOpen, setDialogOpen ] = useState( false );
    const handleDialogOpen = () =>
    {
        setDialogOpen( true );
    };
    const handleDialogClose = () =>
    {
        setDialogOpen( false );
    };
    const [ dayOneDialogOpen, setDayOneDialogOpen ] = useState( false );
    const selectedWaypointInputIndex = useSelectedWaypointInputIndex();
    const isOvernightStop = formParent.getValues( `waypoints.${index}.overnightStop` ) !== undefined;
    const label = ( isFirst ? START_STRING + " " + LOCATION : ( isLast ? END_STRING + " " + LOCATION : isOvernightStop ? OVERNIGHT_STOP : PIT_STOP_STRING ) );
    const isWaypointErrored = Boolean( errorProp( `waypoints.${index}.location.locationName` ).error );

    return (
        <React.Fragment>
            <Grid
                container
                sx = {{ height: "85px", alignItems: "center" }}
                onMouseEnter = {() => setIsHovered( true )}
                onMouseLeave = {() => setIsHovered( false )}
            >
                <Grid item xs = {1}>
                    {drag}
                </Grid>
                <Grid item xs = {isMobile ? 9 : 10}>
                    <Controller
                        control = {control}
                        name = {`waypoints.${index}.location`}
                        render = {() => (
                            <BorderWithLabel
                                {...register( `waypoints.${index}.location` as const, { required: true } )}
                                label = {label + "*"}
                                overrideIsSelected = {index === selectedWaypointInputIndex}
                                errorProp = {errorProp( `waypoints.${index}.location.locationName` )}
                                topOffset = {-10}
                                toolTipText = {LOCATION_TOOLTIP}
                                {...muiTextFieldRegister( `waypoints.${index}.location`, { required: true } )}
                                ref = {null}
                                backgroundColor = {backgroundColor}
                            >
                                <LocationInput
                                    key = {index + ( locationName )}
                                    query = {locationName}
                                    index = {index}
                                    backgroundColor = {backgroundColor}
                                    onChange = {( location ) =>
                                    {
                                        formParent.setValue( `waypoints.${index}.location`, location, {
                                            shouldValidate: true,
                                        } );
                                        if ( location.location.latitude && location.location.longitude )
                                        {
                                            const waypoint = {
                                                location: {
                                                    location: {
                                                        latitude: location.location.latitude,
                                                        longitude: location.location.longitude,
                                                    },
                                                    locationName: location.locationName,
                                                },
                                                overnightStop: {
                                                    minNights: 1,
                                                    maxNights: 1,
                                                },
                                                startTime: undefined,
                                                firstDayAfterWaypointDesiredEndOverride: undefined
                                            };

                                            dispatch( updateWaypoint( { index, waypoint, isDrag: false } ) );
                                        }
                                    }}
                                    setIsInteractingWithTextFieldLocationInput = {setIsInteractingWithTextFieldLocationInput}
                                >
                                    {waypointSize > 2 &&
                                    <Box sx = {{ display: "flex", flexWrap: "nowrap" }}>
                                        <IconButton
                                            color = "error"
                                            onClick = {() =>
                                            {
                                                handleRemove( index );
                                                dispatch( removeWaypoint( { index: index } ) );
                                            }}
                                            sx = {{ alignItems: "start", maxHeight: "40px", left: "45px" }}
                                        >
                                            <ClearIcon/>
                                        </IconButton>
                                    </Box>}
                                </LocationInput>
                            </BorderWithLabel>
                        )}
                    />
                </Grid>
                <Grid item xs = {1}>
                    <React.Fragment>
                        {( isFirst && isDefined ) && (
                            <Tooltip title = {SPECIFY_EARLY_OR_LATE_START} sx = {{ left: "20px", top: "2px" }}>
                                <IconButton color = "secondary" onClick = {() => setDayOneDialogOpen( true )}>
                                    <MoreVertIcon/>
                                </IconButton>
                            </Tooltip>
                        )}
                        {isIntermediate && (
                            <Tooltip title = {EDIT_LENGTH_OF_STAY_TOOLTIP} sx = {{ left: "20px", top: "2px" }}>
                                <IconButton color = "secondary" onClick = {handleDialogOpen}>
                                    <MoreVertIcon/>
                                </IconButton>
                            </Tooltip>
                        )}
                    </React.Fragment>
                </Grid>
                {isMobile &&
                <Grid item xs = {1}>
                    <Button fullWidth = {false} sx = {{ minWidth: "10px", height: "40px", right: "80px", bottom: isWaypointErrored ? "10px" : "" }} onClick = {handleModalOpen} variant = "contained">
                        <FontAwesomeIcon icon = {faLocationDot}/>
                    </Button>
                </Grid>}
            </Grid>

            {modalOpen &&
            <OpenLayersModal
                open = {modalOpen}
                handleClose = {handleModalClose}
                index = {index}
            />}

            <LengthOfStayDialog
                open = {dialogOpen}
                handleClose = {handleDialogClose}
                formParent = {formParent}
                index = {index}
                isIntermediate = {isIntermediate}
                isAdvisory = {isAdvisory}
            />
            <DayOneStartDialog
                open = {dayOneDialogOpen}
                handleClose = {() => setDayOneDialogOpen( false )}
                formParent = {formParent}
                index = {index}
            />
        </React.Fragment>
    );
};
